import { Fragment, useMemo } from 'react';

import { differenceInMinutes, format, subDays } from 'date-fns';

import { InlineDatePicker } from '@components/datepicker';
import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  FilterMenuProps,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { FilterData } from '@components/filters/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconBrandHireflix } from '@components/icons/brand-hireflix';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconIndicator } from '@components/icons/indicator';
import { IconRadioChecked } from '@components/icons/radio-checked';
import { IconRadioIdle } from '@components/icons/radio-idle';
import { MultiSlider, Slider } from '@components/slider';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';

import { useAuthenticatedUser } from 'features/authentication';
import { FilterActionButtons } from 'features/filters/filter-action-buttons';
import { FilterComparerMenu } from 'features/filters/filter-comparer-menu';
import { sortBy } from 'helpers/sorting';
import { useStore } from 'models/Provider';
import { IJob } from 'models/types';

import { useHireflixEnabled } from './hooks';

const RATING_COMPARERS: Array<FilterComparer> = ['is_rated_by', 'not_is_rated_by'];

const FIELDS = [{ key: 'rating' }, { key: 'status' }, { key: 'date_sent' }] as const;

function useValueForData(data: FilterData) {
  const { t, userStore } = useStore();
  const users = userStore.items;

  switch (data.comparer) {
    case 'is_rated_by':
    case 'not_is_rated_by':
      const user = users.get(data.value);
      return user ? user.name : '?';
    case 'between':
      return Array.isArray(data.value) ? `${data.value[0]} - ${data.value[1]}` : '';
    case 'lt':
    case 'gt':
    case 'eq':
      switch (data.field) {
        case 'date_sent':
          return `${data.value} ${t('filters.daysago')}`;
        default:
          return data.value;
      }
    case 'on':
    case 'before':
    case 'after':
      return format(new Date(data.value as string), 'yyyy-MM-dd');
    case 'is':
    case 'not_is':
      return t(`integrations.hireflix.filters.${data.field}.${data.value}`);
    case 'any_of':
    case 'none_of':
      return Array.isArray(data.value)
        ? data.value
            .map((val) => t(`integrations.hireflix.filters.${data.field}.${val}`))
            .join(', ')
        : '';
    case 'today':
      return null;
    default:
      return data.value;
  }
}

function FilterApplicationHireflixApplied({ data }: FilterAppliedProps) {
  const { t } = useStore();

  const value = useValueForData(data);

  const category = `${t('integrations.hireflix.filters.' + data.field)}`;

  return (
    <Filters.AppliedFilter
      icon={<IconBrandHireflix size={24} />}
      category={category}
      value={value && String(value)}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

type RatingProps = {
  users: Array<{ value: string; label: string }>;
};

function FilterApplicationHireflixRatingMenu(props: RatingProps) {
  const { t } = useStore();
  const me = useAuthenticatedUser();
  const { state, actions, isMultiComparer, hasValue } = useCurrentFilter();
  const { users } = props;

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('integrations.hireflix.filters.title')}
        icon={<IconArrowLeft />}
        onClick={() => {
          actions.unselectCategory();
        }}
      />
      <Filters.ListItem
        heading
        label={t('applicants.filters.rating.title')}
        icon={<IconArrowLeft />}
        onClick={() => {
          actions.unselectField();
        }}
      />

      {(state.comparer === 'is_rated_by' || state.comparer === 'not_is_rated_by') && (
        <Filters.ListItem
          heading
          label={t('comparers.' + state.comparer).replace('...', '')}
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
        />
      )}

      <Filters.ListSeparator />

      {!(state.comparer === 'is_rated_by' || state.comparer === 'not_is_rated_by') && (
        <Fragment>
          <Filters.ListItem
            icon={<IconCheckmark color="green200" />}
            label={t('applicants.filters.rating.withmine')}
            onClick={() => {
              actions.applyFilter({ comparer: 'is_rated_by', value: String(me.id) });
            }}
          />

          <Filters.ListItem
            icon={<IconIndicator color="orange200" />}
            label={t('applicants.filters.rating.withoutmine')}
            onClick={() => {
              actions.applyFilter({ comparer: 'not_is_rated_by', value: String(me.id) });
            }}
          />

          <Filters.ListItem
            icon={<IconCloseRegular color="red200" />}
            label={t('applicants.filters.rating.-')}
            onClick={() => {
              actions.applyFilter({ comparer: 'is_none', value: '-' });
            }}
          />
          {state.field && (
            <Fragment>
              <Filters.ListSeparator />

              <Filters.ListItem
                icon={state.comparer === 'gt' ? <IconRadioChecked /> : <IconRadioIdle />}
                key={'gt'}
                label={t('comparers.gt')}
                onClick={() => actions.selectComparer('gt')}
              />
              {state.comparer === 'gt' && (
                <Slider
                  onChange={(val) => actions.selectValue(String(val))}
                  defaultValue={Number(state.value)}
                  minValue={0}
                  maxValue={10}
                  step={0.1}
                  label={t('comparers.gt')}
                />
              )}

              <Filters.ListItem
                icon={state.comparer === 'between' ? <IconRadioChecked /> : <IconRadioIdle />}
                key={'between'}
                label={t('comparers.between')}
                onClick={() => actions.selectComparer('between')}
              />
              {state.comparer === 'between' && (
                <MultiSlider
                  onChange={(val) => actions.selectValue([String(val[0]), String(val[1])])}
                  defaultValue={[
                    state.value ? Number(state.value[0]) : 0,
                    state.value ? Number(state.value[1]) : 10,
                  ]}
                  minValue={0}
                  maxValue={10}
                  step={0.1}
                  label={t('comparers.between')}
                />
              )}

              <Filters.ListItem
                icon={state.comparer === 'lt' ? <IconRadioChecked /> : <IconRadioIdle />}
                key={'lt'}
                label={t('comparers.lt')}
                onClick={() => actions.selectComparer('lt')}
              />
              {state.comparer === 'lt' && (
                <Slider
                  onChange={(val) => actions.selectValue(String(val))}
                  defaultValue={Number(state.value)}
                  minValue={0}
                  maxValue={10}
                  step={0.1}
                  label={t('comparers.lt')}
                />
              )}

              <Filters.ListSeparator />
              <FilterComparerMenu
                comparers={RATING_COMPARERS}
                onComparerSelect={actions.selectComparer}
              />
            </Fragment>
          )}
        </Fragment>
      )}
      {(state.comparer === 'is_rated_by' || state.comparer === 'not_is_rated_by') && (
        <Filters.FilterValueMenu
          items={users}
          isMulti={isMultiComparer}
          onSetValue={actions.selectValue}
          state={state}
        />
      )}
      <FilterActionButtons disableApply={!hasValue} />
    </Filters.List>
  );
}

type ExtraProps = {
  users: Array<{ value: string; label: string }>;
};

function FilterApplicationHireflixMenu({ users }: FilterMenuProps<ExtraProps>) {
  const { state } = useCurrentFilter();

  switch (state.field) {
    case 'rating':
      return <FilterApplicationHireflixRatingMenu users={users} />;
    case 'status':
      return <FilterApplicationHireflixStatusMenu />;
    case 'date_sent':
      return <FilterApplicationHireflixDateSentMenu />;
    default:
      return <FilterApplicationHireflixDefaultMenu />;
  }
}

const STATUS_COMPARERS: Array<FilterComparer> = ['is', 'not_is', 'any_of', 'none_of'];

function FilterApplicationHireflixStatusMenu() {
  const { t } = useStore();
  const { state, actions, isMultiComparer, hasValue } = useCurrentFilter();

  const filterValueItems = ['sent', 'completed', 'finalist', 'archived'].map((item) => ({
    value: item,
    label: t('integrations.hireflix.filters.status.' + item),
  }));

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('integrations.hireflix.filters.title')}
        icon={<IconArrowLeft />}
        onClick={() => {
          actions.unselectCategory();
        }}
      />
      <Filters.ListItem
        heading
        label={t('integrations.hireflix.filters.status')}
        icon={<IconArrowLeft />}
        onClick={() => {
          actions.unselectField();
        }}
      />

      {state.comparer && (
        <Filters.ListItem
          heading
          label={t('comparers.' + state.comparer).replace('...', '')}
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
        />
      )}

      <Filters.ListSeparator />

      {!state.comparer && (
        <FilterComparerMenu
          comparers={STATUS_COMPARERS}
          onComparerSelect={actions.selectComparer}
        />
      )}

      {state.comparer && (
        <Fragment>
          <Filters.FilterValueMenu
            items={filterValueItems}
            isMulti={isMultiComparer}
            onSetValue={actions.selectValue}
            state={state}
          />
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}
    </Filters.List>
  );
}

const ABSOLUTE_COMPARERS: Array<FilterComparer> = ['after', 'on', 'before'];
const RELATIVE_COMPARERS: Array<FilterComparer> = ['gt', 'eq', 'lt'];
const COMBINED_COMPARERS = [...ABSOLUTE_COMPARERS, ...RELATIVE_COMPARERS];

function FilterApplicationHireflixDateSentMenu() {
  const { t, lang } = useStore();
  const { state, actions, hasValue } = useCurrentFilter();

  let disableApply = true;

  if (state.comparer) {
    if (COMBINED_COMPARERS.includes(state.comparer) && hasValue) {
      disableApply = false;
    } else if (['yesterday', 'today'].includes(state.comparer)) {
      disableApply = false;
    }
  }

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('integrations.hireflix.filters.title')}
        icon={<IconArrowLeft />}
        onClick={() => {
          actions.unselectCategory();
        }}
      />
      <Filters.ListItem
        heading
        label={t('integrations.hireflix.filters.date_sent')}
        icon={<IconArrowLeft />}
        onClick={() => {
          actions.unselectField();
        }}
      />

      {state.comparer && ABSOLUTE_COMPARERS.includes(state.comparer) && (
        <Filters.ListItem
          heading
          label={t('comparers.' + state.comparer)}
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
        />
      )}

      <Filters.ListSeparator />

      {state.comparer && ABSOLUTE_COMPARERS.includes(state.comparer) && (
        <InlineDatePicker
          lang={lang}
          value={hasValue ? (state.value as string) : null}
          onChange={(val: Date) => actions.selectValue(val.toISOString())}
        />
      )}
      {!(state.comparer && ABSOLUTE_COMPARERS.includes(state.comparer)) && (
        <Fragment>
          <Filters.SubHeading text={t('applicants.filters.date.rel')} />

          <Filters.ListItem
            icon={state.comparer === 'today' ? <IconRadioChecked /> : <IconRadioIdle />}
            label={t('comparers.today')}
            onClick={() => actions.selectComparer('today')}
          />

          <Filters.ListItem
            icon={state.comparer === 'yesterday' ? <IconRadioChecked /> : <IconRadioIdle />}
            label={t('comparers.yesterday')}
            onClick={() => actions.selectComparer('yesterday')}
          />
          {RELATIVE_COMPARERS.map((comparer) => (
            <Fragment key={comparer}>
              <Filters.ListItem
                icon={state.comparer === comparer ? <IconRadioChecked /> : <IconRadioIdle />}
                label={t('comparers.' + comparer)}
                onClick={() => actions.selectComparer(comparer)}
              />
              {state.comparer === comparer && (
                <Stack space={2} orientation="horizontal" align="center">
                  <TextField
                    autoFocus
                    hideLabel
                    label={t('comparers.' + comparer)}
                    value={state.value as string}
                    onChange={(val) => {
                      if (!val || /^\d+$/.test(val)) {
                        actions.selectValue(val);
                      }
                    }}
                  />
                  <Text font="regular14" color="purple80" text={t('filters.daysago')} />
                </Stack>
              )}
            </Fragment>
          ))}

          <Filters.SubHeading text={t('applicants.filters.date.abs')} />

          {ABSOLUTE_COMPARERS.map((comparer) => (
            <Filters.ListItem
              key={comparer}
              label={t('comparers.' + comparer)}
              onClick={() => actions.selectComparer(comparer)}
            />
          ))}
        </Fragment>
      )}

      <FilterActionButtons disableApply={disableApply} />
    </Filters.List>
  );
}

function FilterApplicationHireflixDefaultMenu() {
  const { t } = useStore();
  const { state, actions } = useCurrentFilter();
  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('integrations.hireflix.filters.title')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      <Filters.ListSeparator />

      {!state.field && (
        <Fragment>
          {FIELDS.map((field) => (
            <Filters.ListItem
              key={field.key}
              label={t('integrations.hireflix.filters.' + field.key)}
              onClick={() => actions.selectField(field.key)}
            />
          ))}
        </Fragment>
      )}
    </Filters.List>
  );
}

export function useFilterApplicationHireflix(job: IJob): FilterConfig<ExtraProps> | null {
  const { t } = useStore();

  const enabled = useHireflixEnabled(job.id);

  return useMemo(() => {
    if (!enabled) return null;

    const users = job.combinedFollowers;
    const sortedUsers = sortBy(
      users.map((user: any) => ({
        value: user.id,
        label: user.name,
      })),
      'name',
      'asc',
    );
    return {
      category: 'hireflix',
      label: t('integrations.hireflix.filters.title'),
      icon: <IconBrandHireflix size={24} />,
      extraProps: { users: sortedUsers },
      AppliedFilter: FilterApplicationHireflixApplied,
      Menu: FilterApplicationHireflixMenu,
      toQueryData: (filter) => {
        const field = filter.field as string;
        const value = filter.value as string;
        const now = new Date();

        switch (filter.comparer) {
          case 'is':
            return { [`integrations_json__hireflix__${field}__is`]: value.toLowerCase() };
          case 'not_is':
            return { [`integrations_json__hireflix__${field}__is_not`]: value.toLowerCase() };
          case 'is_none':
            return { [`integrations_json__hireflix__${field}__is_none`]: '1' };
          case 'gt':
            switch (filter.field) {
              case 'date_sent':
                const xDaysAgo = differenceInMinutes(now, subDays(now, Number(filter.value)));
                return { [`integrations_json__hireflix__date_sent__lte`]: `now-${xDaysAgo}m` };
              default:
                return { [`integrations_json__hireflix__${field}__gte`]: value };
            }
          case 'lt':
            switch (filter.field) {
              case 'date_sent':
                const xDaysAgo = differenceInMinutes(now, subDays(now, Number(filter.value)));
                return { [`integrations_json__hireflix__date_sent__gte`]: `now-${xDaysAgo}m` };
              default:
                return { [`integrations_json__hireflix__${field}__lte`]: value };
            }
          case 'on': {
            const selectedDay = new Date(filter.value as string);
            return {
              integrations_json__hireflix__date_sent__gte: format(
                subDays(selectedDay, 1),
                'yyyy-MM-dd',
              ),
              integrations_json__hireflix__date_sent__lte: format(
                subDays(selectedDay, -1),
                'yyyy-MM-dd',
              ),
            };
          }
          case 'after': {
            const selectedDay = new Date(filter.value as string);
            return {
              integrations_json__hireflix__date_sent__gte: format(selectedDay, 'yyyy-MM-dd'),
            };
          }
          case 'before': {
            const selectedDay = new Date(filter.value as string);
            return {
              integrations_json__hireflix__date_sent__lte: format(selectedDay, 'yyyy-MM-dd'),
            };
          }
          case 'eq': {
            const selectedDayBefore = subDays(now, Number(filter.value) - 1);
            const selectedDayAfter = subDays(now, Number(filter.value) + 1);
            return {
              integrations_json__hireflix__date_sent__lte: format(selectedDayBefore, 'yyyy-MM-dd'),
              integrations_json__hireflix__date_sent__gte: format(selectedDayAfter, 'yyyy-MM-dd'),
            };
          }
          case 'any_of':
            return { [`integrations_json__hireflix__${field}__any_of`]: value };
          case 'none_of':
            return { [`integrations_json__hireflix__${field}__none_of`]: value };
          case 'between':
            return {
              [`integrations_json__hireflix__${field}__gte`]: value[0],
              [`integrations_json__hireflix__${field}__lte`]: value[1],
            };
          case 'is_rated_by':
            return { [`integrations_json__hireflix__users_rated__is`]: value };
          case 'not_is_rated_by':
            return { [`integrations_json__hireflix__users_rated__is_not`]: value };
          case 'yesterday':
            return {
              integrations_json__hireflix__date_sent__gte: format(subDays(now, 2), 'yyyy-MM-dd'),
              integrations_json__hireflix__date_sent__lte: format(now, 'yyyy-MM-dd'),
            };
          case 'today':
            return {
              integrations_json__hireflix__date_sent__gte: format(subDays(now, 1), 'yyyy-MM-dd'),
              integrations_json__hireflix__date_sent__lte: format(subDays(now, -1), 'yyyy-MM-dd'),
            };
          default:
            return {};
        }
      },
    };
  }, [enabled, job.combinedFollowers, t]);
}
